import React, { useCallback } from 'react';
import type {
  UseFormSetValue,
  UseFormWatch,
  Path,
  PathValue,
} from 'react-hook-form';

export const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'];
export const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
export const validateFile = (file: File): boolean => {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return allowedExtensions.includes(ext);
};

type UseAttachmentsProps<TForm extends Record<'attachments', File[]>> =
  {
    setValue: UseFormSetValue<TForm>;
    watch: UseFormWatch<TForm>;
    field: Path<TForm>;
  };

export const useAttachments = <
  TForm extends Record<'attachments', File[]>,
>({
  setValue,
  watch,
  field,
}: UseAttachmentsProps<TForm>) => {
  const formChangeOptions = {
    shouldValidate: true,
    shouldDirty: true,
  };


  const attachments = watch(field as Path<TForm>) ?? [];

  const onAttachmentsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = Array.from(e.target.files ?? []);
      const current = watch(field as Path<TForm>) ?? [];
      setValue(
        field as Path<TForm>,
        [...current, ...newFiles] as PathValue<TForm, Path<TForm>>,
        formChangeOptions
      );
      e.target.value = '';
    },
    [setValue, watch, field]
  );

  const onRemoveAttachment = useCallback(
    (fileToRemove: File) => {
      const current = watch(field as Path<TForm>) ?? [];
      const updated = (current as File[]).filter(
        (file: File) =>
          file.name !== fileToRemove.name ||
          file.size !== fileToRemove.size ||
          file.lastModified !== fileToRemove.lastModified
      );
      setValue(
        field as Path<TForm>,
        updated as PathValue<TForm, Path<TForm>>,
        formChangeOptions
      );
    },
    [setValue, watch, field]
  );

  return {
    attachments,
    onAttachmentsChange,
    onRemoveAttachment,
    validateFile,
  };
};
